import type { ComponentType } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Button, Menu, MenuItem, MenuTrigger, Popover, Tooltip, TooltipTrigger } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';

import { envBaseUrls } from '../../../constants';
import { Avatar } from '../Avatar';
import { DarkModeToggle } from '../DarkModeToggle';
import { Logo } from '../Logo';
import { useOmnibarContext } from '../Omnibar/OmnibarProvider';

import './styles.scss';

// Shared button variant for all TopNav buttons
const TOPNAV_BUTTON_VARIANT = 'minimal' as const;

type MenuItem = Readonly<{
  to: string;
  name: string;
  allowed?: boolean;
}>;

type TopNavProps = Readonly<{
  menuItems: readonly MenuItem[];
  avatar: string | null | undefined;
  searchComponent?: ComponentType | 'omnibar' | null;
  onSignOut: () => void;
}>;

const TopNavMenu = ({ menuItems }: { menuItems: readonly MenuItem[] }) => {
  const navigate = useNavigate();

  return (
    <MenuTrigger>
      <TooltipTrigger>
        <Button variant={TOPNAV_BUTTON_VARIANT}>
          <Icon name="application" />
        </Button>
        <Tooltip>Menu</Tooltip>
      </TooltipTrigger>
      <Popover>
        <Menu>
          {menuItems.map(
            ({ allowed, to, name }: MenuItem) =>
              allowed && (
                <MenuItem
                  key={to}
                  onAction={() => {
                    navigate(to);
                  }}
                >
                  {name}
                </MenuItem>
              ),
          )}
        </Menu>
      </Popover>
    </MenuTrigger>
  );
};

const EnvironmentSwitcher = () => {
  // Detect current environment based on hostname
  const getCurrentEnvironment = () => {
    const { hostname } = window.location;

    switch (hostname) {
      // Preview environment (GitHub PR deployments)
      case 'vitalstatistix-test.launchdarkly.com':
        return 'Preview';

      // Federal environments
      case 'vitalstatistix.stg.launchdarkly.us':
        return 'Federal Staging';
      case 'vitalstatistix.launchdarkly.us':
        return 'Federal Production';

      // EU environment
      case 'vitalstatistix.eu.launchdarkly.com':
        return 'EU Production';

      // Commercial environments
      case 'vitalstatistix-stg.launchdarkly.com':
        return 'Staging';
      case 'vitalstatistix.launchdarkly.com':
        return 'Production';
      case 'vitalstatistix.ld.catamorphic.com':
        return 'Catamorphic';

      // Local development (using includes for localhost variants)
      default:
        if (hostname.includes('localhost') || hostname === '127.0.0.1') {
          return 'Local';
        }
        return 'Production'; // Default fallback
    }
  };

  const currentEnv = getCurrentEnvironment();

  return (
    <MenuTrigger>
      <TooltipTrigger>
        <Button variant={TOPNAV_BUTTON_VARIANT}>
          <Icon name="link" slot="start-icon" />
          {currentEnv}
        </Button>
        <Tooltip>Switch environment</Tooltip>
      </TooltipTrigger>
      <Popover>
        <Menu>
          <MenuItem
            onAction={() => {
              window.location.href = envBaseUrls.staging;
            }}
          >
            Staging
          </MenuItem>
          <MenuItem
            onAction={() => {
              window.location.href = envBaseUrls.production;
            }}
          >
            Production
          </MenuItem>
          <MenuItem
            onAction={() => {
              window.location.href = envBaseUrls.federalStaging;
            }}
          >
            Federal Staging
          </MenuItem>
          <MenuItem
            onAction={() => {
              window.location.href = envBaseUrls.federalProduction;
            }}
          >
            Federal Production
          </MenuItem>
          <MenuItem
            onAction={() => {
              window.location.href = envBaseUrls.euProduction;
            }}
          >
            EU Production
          </MenuItem>
          <MenuItem
            onAction={() => {
              window.location.href = envBaseUrls.catamorphic;
            }}
          >
            Catamorphic
          </MenuItem>
          <MenuItem
            onAction={() => {
              window.location.href = envBaseUrls.localHost;
            }}
          >
            Local
          </MenuItem>
        </Menu>
      </Popover>
    </MenuTrigger>
  );
};

export const TopNav = ({ menuItems, avatar, searchComponent, onSignOut }: TopNavProps) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Get omnibar context for when searchComponent is 'omnibar'
  let omnibarContext;
  try {
    // oxlint-disable-next-line react-hooks/rules-of-hooks, react/hooks -- the hook is called unconditionally; only its missing-provider throw is caught, and useContext itself never suspends or throws
    omnibarContext = useOmnibarContext();
  } catch {
    // Not in omnibar provider context
    omnibarContext = null;
  }

  const renderSearchComponent = () => {
    if (!searchComponent) {
      return null;
    }

    if (searchComponent === 'omnibar' && omnibarContext) {
      const { OmnibarTrigger } = omnibarContext;
      return <OmnibarTrigger variant="default" size="small" showShortcut />;
    }

    if (typeof searchComponent === 'function') {
      const SearchComponent = searchComponent;
      return <SearchComponent />;
    }

    return null;
  };

  return (
    <nav className="TopNav">
      <div className="TopNav-left">
        <div className="TopNav-logo">
          <Button variant={TOPNAV_BUTTON_VARIANT} onPress={() => navigate('/')}>
            <Logo />
            <span className="TopNav-heading">Vitalstatistix</span>
          </Button>
        </div>
        {searchComponent && <div className="TopNav-search">{renderSearchComponent()}</div>}
      </div>

      <div className="TopNav-right">
        {location.pathname === '/provisioning-plan-review' ? '' : <EnvironmentSwitcher />}

        <TopNavMenu menuItems={menuItems} />

        <DarkModeToggle />

        <div className="TopNav-divider" />

        {avatar ? <Avatar email={avatar} /> : <Icon name="person" />}

        <TooltipTrigger>
          <Button variant={TOPNAV_BUTTON_VARIANT} onPress={() => onSignOut()}>
            <Icon name="door-exit" />
          </Button>
          <Tooltip>Sign out</Tooltip>
        </TooltipTrigger>
      </div>
    </nav>
  );
};
